/**
 * AddEventDialog.tsx
 * Dialog for creating a manual Maintenance / Calibration / Out of Service event
 * on a single machine, independent of any quote or work order.
 *
 * Phase 2, Item 6 — "+ Add Event".
 *
 * Fields:
 *   Machine    — dropdown of active machines (MachineKey + Code + Name)
 *   Event Type — Maintenance | Calibration | Out of Service
 *   Start Date — date picker (text input, YYYY-MM-DD)
 *   End Date   — date picker (text input, YYYY-MM-DD)
 *   Duration   — optional minutes/day (G2-6): a 120-min calibration blocks
 *                2 hours instead of the whole day; blank = full-day block
 *   Notes      — optional free text
 *
 * On submit:
 *   POST /api/schedule/bookings/event via createManualEvent() service, with
 *   confirmCheck: true (G2-6 overlap warn). A 409 needsConfirm answer renders
 *   a warning listing the overlapped bookings and turns the button into
 *   "Create Anyway" (resends with force: true) — advisory, never blocks.
 *   On success: call onSuccess() (parent refetches bookings) + close dialog.
 *   On error:   show inline error alert.
 *
 * Color: bars for these events are rendered amber (Maintenance / Calibration)
 * or neutral grey (OutOfService) per theme.schedule.* tokens — no hex literals.
 * That logic already lives in BookingBar.tsx; no change required there.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Stack,
  Alert,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { MachineBookingEventType } from '../../../types/schedule';
import {
  createManualEvent,
  ManualEventConflictError,
  type ManualEventCollision,
} from '../../../services/scheduleBookings';

// ── types ──────────────────────────────────────────────────────────────────

interface MachineSummary {
  machineKey: string;
  code: string;
  name: string;
}

interface AddEventDialogProps {
  open: boolean;
  /** Pre-selected machine key; if provided, the machine dropdown is pre-filled. */
  defaultMachineKey?: string;
  /** Pre-selected start date 'YYYY-MM-DD'; defaults to today. */
  defaultStartDate?: string;
  /** Pre-selected end date 'YYYY-MM-DD'; defaults to the start date. */
  defaultEndDate?: string;
  /** List of active machines for the dropdown. Parent owns this list. */
  machines: MachineSummary[];
  /** Called after a successful event creation so the parent can refetch bookings. */
  onSuccess: () => void;
  /** Called when the user cancels or closes the dialog. */
  onClose: () => void;
}

// ── constants ──────────────────────────────────────────────────────────────

const EVENT_TYPE_OPTIONS: { value: MachineBookingEventType; label: string }[] = [
  { value: 'Maintenance',  label: 'Maintenance' },
  { value: 'Calibration',  label: 'Calibration' },
  { value: 'OutOfService', label: 'Out of Service' },
];

/** Return today as 'YYYY-MM-DD'. */
function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

/** G2-6: duration ceiling — one full working day in minutes. */
const MAX_EVENT_MINUTES = 480;

/** Short human label for one overlapped booking in the G2-6 warning list. */
function collisionLabel(c: ManualEventCollision): string {
  const what = c.TaskName ?? c.EventType ?? 'Booking';
  const span = c.StartDate === c.EndDate ? c.StartDate : `${c.StartDate} → ${c.EndDate}`;
  return `${what} (${c.Status}, ${span})`;
}

// ── component ──────────────────────────────────────────────────────────────

export const AddEventDialog: React.FC<AddEventDialogProps> = ({
  open,
  defaultMachineKey,
  defaultStartDate,
  defaultEndDate,
  machines,
  onSuccess,
  onClose,
}) => {
  const theme = useTheme();

  // ── form state ───────────────────────────────────────────────────────────

  const [machineKey, setMachineKey] = useState<string>(defaultMachineKey ?? '');
  const [eventType, setEventType] = useState<MachineBookingEventType>('Maintenance');
  const [startDate, setStartDate] = useState<string>(defaultStartDate ?? todayISO());
  const [endDate, setEndDate] = useState<string>(defaultEndDate ?? defaultStartDate ?? todayISO());
  /** G2-6: optional duration in minutes per covered day ('' = full-day block). */
  const [durationMinutes, setDurationMinutes] = useState<string>('');
  const [notes, setNotes] = useState<string>('');

  // ── async state ──────────────────────────────────────────────────────────

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** G2-6: collisions from the 409 needsConfirm answer (null = no warning shown). */
  const [collisions, setCollisions] = useState<ManualEventCollision[] | null>(null);

  // ── reset form when dialog opens ─────────────────────────────────────────

  useEffect(() => {
    if (open) {
      setMachineKey(defaultMachineKey ?? '');
      setEventType('Maintenance');
      setStartDate(defaultStartDate ?? todayISO());
      setEndDate(defaultEndDate ?? defaultStartDate ?? todayISO());
      setDurationMinutes('');
      setNotes('');
      setError(null);
      setCollisions(null);
    }
  }, [open, defaultMachineKey, defaultStartDate, defaultEndDate]);

  // ── validation ────────────────────────────────────────────────────────────

  function validate(): string | null {
    if (!machineKey) return 'Please select a machine.';
    if (!startDate) return 'Start date is required.';
    if (!endDate) return 'End date is required.';
    if (endDate < startDate) return 'End date must be on or after start date.';
    if (durationMinutes.trim() !== '') {
      const m = Number(durationMinutes);
      if (!Number.isInteger(m) || m < 1 || m > MAX_EVENT_MINUTES) {
        return `Duration must be a whole number of minutes between 1 and ${MAX_EVENT_MINUTES}.`;
      }
    }
    return null;
  }

  // ── submit ────────────────────────────────────────────────────────────────

  /**
   * G2-6: first attempt sends confirmCheck: true — the API answers 409
   * needsConfirm with the overlapped bookings instead of silently creating a
   * hard conflict. "Create Anyway" re-invokes with force: true.
   */
  async function handleSubmit(force = false) {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await createManualEvent({
        machineKey,
        eventType,
        startDate,
        endDate,
        notes: notes.trim() || undefined,
        eventMinutes: durationMinutes.trim() !== '' ? Number(durationMinutes) : undefined,
        confirmCheck: true,
        force,
      });
      onSuccess();
      onClose();
    } catch (err) {
      if (err instanceof ManualEventConflictError) {
        setCollisions(err.collisions);
      } else {
        setCollisions(null);
        setError(err instanceof Error ? err.message : 'Failed to create event. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  // ── color hint for event type ─────────────────────────────────────────────

  function eventTypeColorDot(et: MachineBookingEventType): React.CSSProperties {
    const color =
      et === 'OutOfService'
        ? theme.schedule.noActivity
        : theme.schedule.maintenance;
    return {
      display: 'inline-block',
      width: 10,
      height: 10,
      borderRadius: '50%',
      backgroundColor: color,
      marginRight: 6,
      verticalAlign: 'middle',
      flexShrink: 0,
    };
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="add-event-dialog-title"
    >
      <DialogTitle id="add-event-dialog-title" sx={{ pb: 1 }}>
        Add Machine Event
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Stack spacing={2.5}>
          {/* Error alert */}
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* G2-6: overlap warning (409 needsConfirm) — advisory, never blocks */}
          {collisions && (
            <Alert severity="warning" onClose={() => setCollisions(null)}>
              <Typography variant="body2" fontWeight={600}>
                This event overlaps {collisions.length} existing booking
                {collisions.length !== 1 ? 's' : ''} on this machine:
              </Typography>
              <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                {collisions.slice(0, 5).map((c) => (
                  <li key={c.MachineBookingKey}>
                    <Typography variant="caption">{collisionLabel(c)}</Typography>
                  </li>
                ))}
                {collisions.length > 5 && (
                  <li>
                    <Typography variant="caption">…and {collisions.length - 5} more</Typography>
                  </li>
                )}
              </ul>
              <Typography variant="caption" color="text.secondary">
                Pick different dates or a different machine, or create it anyway.
              </Typography>
            </Alert>
          )}

          {/* Machine dropdown */}
          <TextField
            select
            label="Machine"
            value={machineKey}
            onChange={(e) => { setMachineKey(e.target.value); setCollisions(null); }}
            size="small"
            fullWidth
            required
            disabled={submitting}
          >
            {machines.length === 0 && (
              <MenuItem value="" disabled>
                No active machines found
              </MenuItem>
            )}
            {machines.map((m) => (
              <MenuItem key={m.machineKey} value={m.machineKey}>
                <Typography variant="body2" component="span">
                  <strong>{m.code}</strong> — {m.name}
                </Typography>
              </MenuItem>
            ))}
          </TextField>

          {/* Event type dropdown */}
          <TextField
            select
            label="Event Type"
            value={eventType}
            onChange={(e) => setEventType(e.target.value as MachineBookingEventType)}
            size="small"
            fullWidth
            required
            disabled={submitting}
          >
            {EVENT_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                <span style={eventTypeColorDot(opt.value)} aria-hidden="true" />
                {opt.label}
              </MenuItem>
            ))}
          </TextField>

          {/* Start date */}
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              // Auto-advance end date if it would become invalid.
              if (e.target.value > endDate) setEndDate(e.target.value);
              // G2-6: a changed span invalidates a previous overlap warning.
              setCollisions(null);
            }}
            size="small"
            fullWidth
            required
            disabled={submitting}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: endDate || undefined }}
          />

          {/* End date */}
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => { setEndDate(e.target.value); setCollisions(null); }}
            size="small"
            fullWidth
            required
            disabled={submitting}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: startDate || undefined }}
          />

          {/* Duration in minutes per day (optional, G2-6) */}
          <TextField
            label="Duration (minutes per day, optional)"
            type="number"
            value={durationMinutes}
            onChange={(e) => setDurationMinutes(e.target.value)}
            size="small"
            fullWidth
            disabled={submitting}
            inputProps={{ min: 1, max: MAX_EVENT_MINUTES, step: 1 }}
            helperText={`Machine time the event blocks on each covered day. Blank = the whole day (${MAX_EVENT_MINUTES} min).`}
            placeholder="e.g. 120 for a 2-hour calibration"
          />

          {/* Notes (optional) */}
          <TextField
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={2}
            disabled={submitting}
            placeholder="e.g. Annual calibration by vendor"
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={collisions ? 'warning' : 'primary'}
          onClick={() => handleSubmit(collisions !== null)}
          disabled={submitting || !machineKey}
          startIcon={submitting ? <CircularProgress size={14} thickness={4} /> : undefined}
          sx={{ textTransform: 'none', minWidth: 100 }}
        >
          {submitting ? 'Saving…' : collisions ? 'Create Anyway' : 'Add Event'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
